import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import { DEFAULT_PAGINATION, FILE_NAME_TYPE } from '../../commons/constants/constants';
import { uploadFiles } from '../../services/uploader.service';
import { PageRequest } from '../../types/CommonTypes';
import { storeName as entityStore, storeName as promotionEntityStore } from '../entities/promotion.entity';
import { RootModel } from '../models';

export const storeName = 'promotions';

const initialState = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  loading: false,
  visible: false,
  editId: null,

  data: [],
  edit: {
    visible: false,
    item: null,
  },
};

export const promotions: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setItem: produce((state, payload) => {
      state.edit = {
        ...state.edit,
        visible: !!payload,
        item: payload,
      };
    }),
  },
  effects: (dispatch: RematchDispatch<typeof promotions>) => {
    return ({
      async reloadList(payload, state) {
        const { criteria, pagination } = state[storeName];
        await this.filterAsync({ ...pagination, ...criteria });
      },
      async resetFilter(payload, state) {
        const { pagination } = state[storeName];
        await this.filterAsync({ ...pagination, page: 0 });
      },
      async pagingAsync(payload: PageRequest, state) {
        const { criteria = {}, pagination = {} } = state[storeName];
        await this.filterAsync({ ...pagination, ...criteria, ...payload });
      },
      async filterAsync(params = {}, state) {
        this.setLoading(true);
        try {
          const data = await dispatch[entityStore].load(params);
          if (data && data.content) {
            this.setIds(data.content.map((itemInfo) => itemInfo.id));
            this.setPagination((({ content, ...otherProps }) => otherProps)(data));
            this.setCriteria(params);
          }
        } catch (e) {
          console.error(e);
          message.error('Failed to search promotion');
        } finally {
          this.setLoading(false);
        }
      },
      async getEditPromotion(id) {
        this.setLoading(true);
        try {
          const promotion = await dispatch[promotionEntityStore].loadById(id);
          this.setEditId(promotion.id);
        } catch (e) {
          console.error(e);
          message.error('Fail to get promotion');
        } finally {
          this.setLoading(false);
        }
      },
      async savePromotion(payload, state) {
        if (!payload) {
          return null;
        }

        this.setLoading(true);
        try {
          const { pagination } = state[storeName];
          const { user } = state.user;
          const editing = !!payload.id;
          const uploadActions = [];
          const data = { ...payload };
          if (data) {
            const { image, icon, id, translations } = data;
            if (icon) {
              const promiseFile = uploadFiles(
                id,
                [icon],
                FILE_NAME_TYPE.ICON,
                user.id,
                'promotion/icon',
              ).then((result) => {
                const { filesList } = result;
                if (filesList.length) {
                  const firstFile = filesList[0];
                  data.icon = { id: firstFile.id, url: firstFile.url };
                }
              });
              uploadActions.push(promiseFile);
            }

            if (image) {
              const promiseFile = uploadFiles(
                id,
                [image],
                FILE_NAME_TYPE.IMAGE,
                user.id,
                'promotion/image',
              ).then((result) => {
                const { filesList } = result;
                if (filesList.length) {
                  const firstFile = filesList[0];
                  data.image = { id: firstFile.id, url: firstFile.url };
                }
              });
              uploadActions.push(promiseFile);
            }

            translations.forEach((v) => {
              if (!v.image) {
                return false;
              }

              const promiseFile = uploadFiles(
                id,
                [v.image],
                FILE_NAME_TYPE.IMAGE,
                user.id,
                'promotion/image',
              ).then((result) => {
                const { filesList } = result;
                if (filesList.length) {
                  const firstFile = filesList[0];
                  v.image = { id: firstFile.id, url: firstFile.url };
                }
              });
              uploadActions.push(promiseFile);
            });
          }

          await axios.all(uploadActions);
          let result;
          if (editing) {
            result = await dispatch[entityStore].update(data);
          } else {
            result = await dispatch[entityStore].create(data);
          }
          message.success('Success to save promotion');
          this.setCriteria({});
          this.filterAsync({
            ...pagination,
            page: 0,
          });
          return result;
        } catch (e) {
          message.error('Fail to save promotion');
          console.error('Fail to save promotion: ', e);
        } finally {
          this.setLoading(false);
        }

        return null;
      },
      async deletePromotion(payload, state) {
        if (!payload) {
          return null;
        }

        try {
          this.setLoading(true);
          await dispatch[entityStore].delete(payload.id);
          message.success(`Success to delete promotion ${payload.code}`);
          this.reloadList();
        } catch (e) {
          message.error(`Fail to delete promotion ${payload.code}`);
          console.error(`Fail to delete promotion ${payload.code}: `, e);
        } finally {
          this.setLoading(false);
        }
      },
    });
  },
});

export default promotions;
